/**
 * Pricing Reference & Estimates
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * SINGLE SOURCE OF TRUTH
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Every model's API cost and the credits-per-cost formula live in ONE place:
 *   server/src/shared/pricing/modelPricing.ts
 *
 * This file no longer mirrors per-model prices (the old hand-maintained
 * API_COSTS / CREDIT_COSTS tables were the source of pricing drift across
 * flows and have been removed). The runtime charge comes from the Supabase
 * `credit_costs` table, which is itself generated from modelPricing.ts. The
 * frontend reads it via `useCreditCosts`.
 *
 * What remains here is only what the pricing/checkout UI needs, and the
 * generation-count estimates are DERIVED from modelPricing so they can never
 * drift again.
 *
 * To change a price: edit modelPricing.ts, regenerate the migration + run the
 * seed (`npx tsx server/scripts/seed-credit-costs.ts`). Nothing here.
 */

import {
  MODEL_API_COST_USD,
  creditsFromApiCost,
  CREDIT_VALUE_USD as CREDIT_VALUE_USD_SOT,
} from "@shared/pricing/modelPricing";

/** $0.02 per credit (Starter rate). Re-exported from the SoT. */
export const CREDIT_VALUE_USD = CREDIT_VALUE_USD_SOT;

/** Derive credit cost from a USD API cost. Re-exported from the SoT. */
export const calculateCredits = creditsFromApiCost;

// ---------------------------------------------------------------------------
// Coupon restrictions
// ---------------------------------------------------------------------------

/** When true, coupon codes are blocked for annual plans (they already have 20% off). */
export const COUPON_BLOCKED_FOR_ANNUAL = true;

// ---------------------------------------------------------------------------
// Pricing page generation estimates — DERIVED from modelPricing (USED IN APP)
// ---------------------------------------------------------------------------

function creditRange(usdValues: number[]): { minCredits: number; maxCredits: number } {
  const credits = usdValues.map(creditsFromApiCost);
  return { minCredits: Math.min(...credits), maxCredits: Math.max(...credits) };
}

const imageUsd: number[] = [];
const shortVideoUsd: number[] = []; // Kling-tier (excludes Sora / Sora Pro premium)
for (const [modelId, p] of Object.entries(MODEL_API_COST_USD)) {
  if (p.image) imageUsd.push(...Object.values(p.image));
  if (p.video && modelId.startsWith("kling-")) {
    shortVideoUsd.push(...Object.values(p.video));
  }
}

/**
 * Min/max credits per generation, used for the "≈ N images / videos" ranges
 * on the pricing & checkout pages. Computed from modelPricing so adding or
 * repricing a model updates these automatically.
 */
export const GENERATION_ESTIMATES = {
  image: creditRange(imageUsd),
  /** Short videos = Kling-tier (excludes Sora Pro which is premium). */
  shortVideo: creditRange(shortVideoUsd),
} as const;

/** Estimate how many generations a token count can produce. */
export function estimateRange(
  tokens: number,
  minCost: number,
  maxCost: number,
): { min: number; max: number } {
  return {
    min: Math.floor(tokens / maxCost),
    max: Math.floor(tokens / minCost),
  };
}
